import { Cause, Clock, Duration, Effect, Exit, Option } from 'effect';

import { Cart } from '../../capability/cart/Cart';
import type { Cart as CartView } from '../../capability/cart/cartSchema';
import { CartError } from '../../capability/cart/errors';
import type { CartWriteError } from '../../capability/cart/errors';
import {
  CART_WRITE_SETTLE_DELAY,
  CART_WRITE_TIMEOUT,
  isUnknownWriteOutcome,
} from './cartTransport';

/**
 * Run one cart write within the transport budget. If its outcome is unknown,
 * confirm it from the cart without sending the write again.
 *
 * The write and its confirmation share one deadline, {@link CART_WRITE_TIMEOUT}
 * after the write starts. The backend cannot commit the write after that
 * deadline, so the caller's cart permit stays taken until the outcome is known.
 * The first confirmation read waits {@link CART_WRITE_SETTLE_DELAY}. Later reads
 * back off.
 */
export const confirmCartWrite = (
  token: string,
  write: Effect.Effect<CartView, CartWriteError, Cart>,
  matches: (cart: CartView) => boolean
): Effect.Effect<CartView, CartWriteError, Cart> =>
  Effect.gen(function* () {
    const deadline = (yield* Clock.currentTimeMillis) + Duration.toMillis(CART_WRITE_TIMEOUT);
    const bounded = write.pipe(
      Effect.timeout(CART_WRITE_TIMEOUT),
      Effect.mapError((error) =>
        Cause.isTimeoutError(error)
          ? new CartError.Unavailable({
              operation: 'write',
              reference: Option.some(token),
              cause: error,
            })
          : error
      )
    );
    const result = yield* Effect.exit(bounded);
    if (Exit.isSuccess(result)) return result.value;

    const unknown = result.cause.reasons.some(
      (reason) => Cause.isFailReason(reason) && isUnknownWriteOutcome(reason.error)
    );
    if (!unknown) return yield* Effect.failCause(result.cause);

    const cartService = yield* Cart;
    let delay = Duration.toMillis(CART_WRITE_SETTLE_DELAY);

    while (true) {
      const remaining = deadline - (yield* Clock.currentTimeMillis);
      if (remaining > 0) yield* Effect.sleep(Duration.millis(Math.min(delay, remaining)));

      const observed = yield* cartService.cart(token).pipe(Effect.option);
      if (Option.isSome(observed) && matches(observed.value)) return observed.value;
      if ((yield* Clock.currentTimeMillis) >= deadline)
        return yield* Effect.failCause(result.cause);

      delay *= 2;
    }
  });
